import React, { useState } from "react";
import { contactInfo, subjects } from "./contactus-data";

const ContactUs = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const required = {
    name: true,
    email: true,
    subject: true,
    message: true,
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  const hasError = (field) =>
    required[field] && !form[field] && (touched[field] || submitted);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      subject: form.subject,
      message: form.message,
    };

    if (!Object.keys(required).every((key) => form[key])) {
      return; // Don't submit if required fields are missing
    }

    try {
      const res = await fetch("/.netlify/functions/sendContactUsEmail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.text();
        console.error("Server returned error:", errorData);
        alert("Failed to send message. Please try again later.");
        return;
      }

      const result = await res.json();
      console.log("Message sent successfully:", result);
      alert("Thank you! Your message has been sent.");

      // Optionally reset your form here:
      // setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      console.error("Unexpected error:", error);
      alert("An unexpected error occurred. Please try again later.");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gray-500 py-60 flex flex-col max-h-96 items-center justify-center text-center">
        <h1 className="text-5xl md:text-6xl text-white mb-4">
          Get in Touch
        </h1>
        <p className="text-xl text-gray-100 font-medium">
          We'd Love to Hear from You
        </p>
      </section>
      {/* Contact Info Cards */}
      <section className="py-16 bg-white">
        <div className="flex flex-wrap justify-center gap-8 w-11/12 max-w-5xl mx-auto">
          {contactInfo.map((info, idx) => (
            <div
              key={info.title}
              className="bg-white rounded-xl shadow-md px-10 py-10 flex flex-col items-center w-full max-w-xs min-w-[260px]"
            >
              {info.icon}
              <div className="font-bold text-xl md:text-2xl text-gray-800 mb-2">
                {info.title}
              </div>
              <div className="text-gray-600 text-center text-base leading-relaxed flex-1">
                {info.details}
              </div>
              {info.phone && (
                <div className="text-gray-800 text-center text-base mt-3 font-medium">
                  {info.phone}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
      {/* Contact Form */}
      <section className="bg-gray-100 py-16 space-y-8 shadow-lg">
        <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-md p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl text-gray-900 mb-2">
              Send Us a Message
            </h2>
            <div className="mx-auto w-20 h-1 bg-orange-500 rounded mb-2"></div>
          </div>
          <form
            onSubmit={handleSubmit}
            noValidate
            className="space-y-6 text-left justify-start" netlify
          >
            <div className="mb-4">
              <label className="block font-semibold mb-1 text-gray-700">
                Full Name <span className="text-orange-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full border rounded px-3 py-2 focus:outline-none ${
                  hasError("name") ? "border-orange-500" : "border-gray-300"
                }`}
                autoComplete="off"
              />
            </div>
            <div className="mb-4">
              <label className="block font-semibold mb-1 text-gray-700">
                Email Address <span className="text-orange-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full border rounded px-3 py-2 focus:outline-none ${
                  hasError("email") ? "border-orange-500" : "border-gray-300"
                }`}
                autoComplete="off"
              />
            </div>
            <div className="mb-4">
              <label className="block font-semibold mb-1 text-gray-700">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none"
                autoComplete="off"
              />
            </div>
            <div className="mb-4">
              <label className="block font-semibold mb-1 text-gray-700">
                Subject <span className="text-orange-500">*</span>
              </label>
              <select
                name="subject"
                value={form.subject}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full border rounded px-3 py-2 focus:outline-none ${
                  hasError("subject") ? "border-orange-500" : "border-gray-300"
                }`}
              >
                <option value="">Select a subject</option>
                {subjects.slice(1).map((subj) => (
                  <option key={subj} value={subj}>
                    {subj}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block font-semibold mb-1 text-gray-700">
                Message <span className="text-orange-500">*</span>
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={4}
                className={`w-full border rounded px-3 py-2 focus:outline-none ${
                  hasError("message") ? "border-orange-500" : "border-gray-300"
                }`}
              />
            </div>
            <div className="mb-4 text-xs text-gray-500">* Required fields</div>
            <button
              type="submit"
              className="w-full bg-orange-500 text-white font-semibold py-3 rounded transition-colors duration-200"
            >
              Send Message
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
